import React, { useCallback, useContext, useMemo } from "react";
import HomeSlider from "./HomeSlider";
import FluidMorphTabs from "../../components/FluidMorphTabs";
import BestCollection from "../../components/BestCollection";
import FoodThemeSection from "../../components/FoodThemeSection";
import TechProductSection from "../../components/TechProductSection";
import BottomNav from "../../components/BottomNav";
import { CartContext } from "../CartPage/CartContext";

const normalizeKey = (s) => String(s || "").toLowerCase().replace(/\s+/g, "").replace(/[^a-z0-9]/g, "");

const CANONICAL_TAB_TITLES = {
  "3amigos": "3amigos",
  "3amigo": "3amigos",
  "3amigosfood": "3amigos",
  "3amigosproducts": "3amigos",
  "3amigositems": "3amigos",
  "3amigosmenu": "3amigos",
  "3amigosrestaurant": "3amigos",
  "threeamigos": "3amigos",
  "food": "3amigos",
  "foods": "3amigos",
  "fooditems": "3amigos",
  "btech": "Btech Projects",
  "btechprojects": "Btech Projects",
  "btechproject": "Btech Projects",
  "techbeginproducts": "Techbegin Products",
  "techbegin": "Techbegin Products",
  "smartphone": "Smartphone",
  "smartphonerepair": "Smartphone",
  "woodwork": "Woodwork",
  "woodproducts": "Woodwork",
};

const toDisplayTitle = (rawKey) => {
  const key = normalizeKey(rawKey);
  if (CANONICAL_TAB_TITLES[key]) return CANONICAL_TAB_TITLES[key];

  if (!rawKey) return "General";
  return String(rawKey)
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join(" ");
};

const Home = ({ navigate, searchQuery, products, productsLoading, productsError, foodMode }) => {
  const { cartItems } = useContext(CartContext);

  const tabs = useMemo(() => {
    if (productsLoading) return [];
    if (!products || products.length === 0) return [];

    const grouped = products.reduce((acc, product) => {
      const rawKey =
        product.category ||
        product.categoryName ||
        (product.categoryId && typeof product.categoryId === "object" ? product.categoryId.name : product.categoryId) ||
        "General";
      const key = toDisplayTitle(rawKey);
      if (!acc[key]) acc[key] = [];
      acc[key].push(product);
      return acc;
    }, {});

    return Object.keys(grouped).map((title) => ({
      id: normalizeKey(title),
      title,
      content: title,
      products: grouped[title],
    }));
  }, [products, productsLoading]);
  
  // Get food products for the FoodThemeSection
  const foodProducts = useMemo(() => {
    if (!products || products.length === 0) return [];
    return products.filter((p) => {
      const cat = normalizeKey(
        p.category ||
        p.categoryName ||
        (p.categoryId && typeof p.categoryId === "object" ? p.categoryId.name : p.categoryId) ||
        ""
      );
      return CANONICAL_TAB_TITLES[cat] === "3amigos";
    });
  }, [products]);

  // Helper function to get products by category
  const getProductsByCategory = useCallback((categoryTitle) => {
    if (!products || products.length === 0) return [];
    return products.filter((p) => {
      const cat = normalizeKey(
        p.category ||
        p.categoryName ||
        (p.categoryId && typeof p.categoryId === "object" ? p.categoryId.name : p.categoryId) ||
        ""
      );
      return CANONICAL_TAB_TITLES[cat] === categoryTitle;
    });
  }, [products]);

  // Get tech category products
  const techbeginProducts = useMemo(() => getProductsByCategory("Techbegin Products"), [getProductsByCategory]);
  const btechProducts = useMemo(() => getProductsByCategory("Btech Projects"), [getProductsByCategory]);
  const smartphoneProducts = useMemo(() => getProductsByCategory("Smartphone"), [getProductsByCategory]);
  const woodworkProducts = useMemo(() => getProductsByCategory("Woodwork"), [getProductsByCategory]);

  return (
    <>
      <HomeSlider foodMode={foodMode} />
      {/* Wire up navigate for FluidMorphTabs */}
      <FluidMorphTabs tabs={tabs} loading={productsLoading} navigate={navigate} foodMode={foodMode} />
      
      {foodMode && (
        <>
          {/* Best Collection - only show on food mode */}
          {!productsLoading && foodProducts.length > 0 && (
            <BestCollection
              products={foodProducts}
              navigate={navigate}
            />
          )}
          
          <div className="mt-32 md:mt-40">
            <FoodThemeSection
              products={foodProducts}
              navigate={navigate}
            />
          </div>
        </>
      )}

      {!foodMode && (
        <div>
          {/* Techbegin Products Section */}
          {techbeginProducts.length > 0 && (
            <TechProductSection
              products={techbeginProducts}
              navigate={navigate}
              title="TechBegin Products"
              subtitle="Latest tech gadgets and accessories for your lifestyle"
              categoryColor="blue"
            />
          )}

          {/* Smartphones Section */}
          {smartphoneProducts.length > 0 && (
            <TechProductSection
              products={smartphoneProducts}
              navigate={navigate}
              title="Smartphones"
              subtitle="High-end smartphones and mobile devices"
              categoryColor="purple"
            />
          )}

          {/* Btech Projects Section */}
          {btechProducts.length > 0 && (
            <TechProductSection
              products={btechProducts}
              navigate={navigate}
              title="B.Tech Projects"
              subtitle="Engineering projects and materials"
              categoryColor="indigo"
            />
          )}

          {/* Woodwork Section */}
          {woodworkProducts.length > 0 && (
            <TechProductSection
              products={woodworkProducts}
              navigate={navigate}
              title="Woodwork"
              subtitle="Handcrafted wooden products and furniture"
              categoryColor="cyan"
            />
          )}
        </div>
      )}

      <BottomNav navigate={navigate} currentPage={"products"} cartItemCount={cartItems.length} />
    </>
  );
};

export default Home;
